import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import PublicDemo from "./PublicDemo";
import { Nav } from "../components/landing/Nav";import { Hero } from "../components/landing/Hero";
import { Problem } from "../components/landing/Problem";
import { HowItWorks } from "../components/landing/HowItWorks";
import { FeatureGrid } from "../components/landing/FeatureGrid";
import { Proof } from "../components/landing/Proof";
import { Pricing } from "../components/landing/Pricing";
import { FAQ } from "../components/landing/FAQ";
import { FinalCta } from "../components/landing/FinalCta";
import { Footer, StickyMobileCta } from "../components/landing/Footer";
import { StickyValueBar } from "../components/landing/StickyValueBar";
import SectionLabel from "../components/landing/SectionLabel";

/* ===== URL-param → banner side-effects (Shopify installed) ===== */
const useQueryParamBanner = (setBanner) => {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get("installed")) {
      setBanner({ type: "success", msg: `Shopify connected · ${q.get("installed")}` });
      setTimeout(() => navigate("/landing", { replace: true }), 6000);
    }
  }, []);
};

const Landing = () => {
  const [scrolled, setScrolled] = useState(false);
  const [showMobileCta, setShowMobileCta] = useState(false);
  const [showValueBar, setShowValueBar] = useState(false);
  const [mobileCtaDismissed, setMobileCtaDismissed] = useState(false);
  const [valueBarDismissed, setValueBarDismissed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [banner, setBanner] = useState(null);
  useQueryParamBanner(setBanner);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      // Show sticky CTAs after ~hero height on the marketing side (both mobile & desktop)
      setShowMobileCta(y > 520);
      setShowValueBar(y > 900);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileNavOpen(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  // Nav active-section highlight
  const [activeSection, setActiveSection] = useState("");
  useEffect(() => {
    const ids = ["overview", "problem", "how", "features", "proof", "pricing", "faq"];
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div
      data-testid="landing-root"
      className="min-h-screen bg-white text-slate-900 antialiased overflow-x-hidden"
      style={{
        fontFamily:
          "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap" />
      <style>{`
        @keyframes lift { 0% { opacity: 0; transform: translateY(20px);} 100% { opacity: 1; transform: translateY(0);} }
        @keyframes fadein { 0% { opacity: 0;} 100% { opacity: 1;} }
        @keyframes ring-pulse { 0% { box-shadow: 0 0 0 0 rgba(16,185,129,0.5);} 100% { box-shadow: 0 0 0 14px rgba(16,185,129,0);} }
        .reveal { animation: lift 700ms cubic-bezier(.2,.8,.2,1) both; }
      `}</style>

      {banner && (
        <div
          data-testid="status-banner"
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 rounded-full backdrop-blur-xl border text-xs font-semibold tracking-wide ${
            banner.type === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"
          }`}
        >
          <span className="inline-flex items-center gap-2">
            <span className={`w-1.5 h-1.5 rounded-full ${banner.type === "success" ? "bg-emerald-500" : "bg-amber-500"} animate-pulse`} />
            {banner.msg}
          </span>
        </div>
      )}

      <Nav scrolled={scrolled} mobileNavOpen={mobileNavOpen} setMobileNavOpen={setMobileNavOpen} activeSection={activeSection} />

      {/* 1. HERO */}
      <Hero />

      {/* Overview video · sits right below hero */}
      <section
        id="overview"
        data-testid="overview-section"
        className="relative px-6 md:px-10 py-14 md:py-20 border-t border-slate-100 bg-slate-50"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6 md:mb-8">
            <SectionLabel>45s · growth walkthrough</SectionLabel>
            <h2 className="text-3xl md:text-5xl lg:text-6xl text-slate-900 mt-4 tracking-tight font-bold leading-[1.05]">
              Grow revenue.<br className="hidden md:block" />
              <span className="text-emerald-600">Expand margin.</span> In one click.
            </h2>
            <p className="mt-3 text-slate-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              Diagnose → 1-click Implement → Revenue $48.7k → $88.6k · Net margin 48% → 53.7% · ROI 7.32x. Every number is real, pulled from the live platform.
            </p>
          </div>
          <div
            data-testid="embedded-demo-wrapper"
            className="relative rounded-2xl md:rounded-3xl p-1 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 border border-slate-200 shadow-xl shadow-emerald-500/10"
          >
            <div className="absolute -top-3 left-4 md:left-6 flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-emerald-200 text-[10px] tracking-[0.18em] uppercase text-emerald-700 z-10 font-bold shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live · 45s System Overview
            </div>
            <div className="h-[460px] sm:h-[540px] md:h-[620px]"><PublicDemo /></div>
          </div>
        </div>
      </section>

      {/* 2. PROBLEM */}
      <Problem />

      {/* 3. HOW IT WORKS */}
      <HowItWorks />

      {/* 4. FEATURE GRID */}
      <FeatureGrid />

      {/* 5. PROOF */}
      <Proof />

      {/* 6. PRICING */}
      <Pricing />

      {/* 7. FAQ */}
      <FAQ />

      {/* 8. FINAL CTA */}
      <FinalCta />

      <Footer />

      {/* Sticky value bar (desktop) + install pill (mobile) */}
      <StickyValueBar
        visible={showValueBar}
        dismissed={valueBarDismissed}
        onDismiss={() => setValueBarDismissed(true)}
      />
      <StickyMobileCta
        visible={showMobileCta}
        dismissed={mobileCtaDismissed}
        onDismiss={() => setMobileCtaDismissed(true)}
      />
    </div>
  );
};

export default Landing;
